import React from 'react';
import { TrendingUp, TrendingDown, Gift, Heart, CreditCard, Wallet, Home, PiggyBank } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const DashboardPreview = () => {
  const { t, isRTL } = useLanguage();

  const metrics = [
    { 
      label: t.dashboardPreview.metrics.totalBalance, 
      value: 'AED 124,850', 
      change: '+2.4%', 
      positive: true,
      icon: Wallet 
    },
    { 
      label: t.dashboardPreview.metrics.monthlySpend, 
      value: 'AED 8,420', 
      change: '-12%', 
      positive: true,
      icon: CreditCard 
    },
    { 
      label: t.dashboardPreview.metrics.rewardsEarned, 
      value: 'AED 342', 
      change: '+18%', 
      positive: true,
      icon: Gift 
    },
    { 
      label: t.dashboardPreview.metrics.creditHealth, 
      value: 'Excellent', 
      change: '780', 
      positive: true,
      icon: Heart 
    }
  ];

  const accounts = [
    { name: 'ADCB Mortgage', balance: 'AED 892,000', type: 'liability', icon: Home, color: '#0071E3' },
    { name: 'Emirates NBD Savings', balance: 'AED 45,200', type: 'asset', icon: PiggyBank, color: '#34C759' },
    { name: 'FAB Credit Card', balance: 'AED 12,400', type: 'liability', icon: CreditCard, color: '#5856D6' }
  ];

  return (
    <section className="py-20 md:py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div className={`text-center mb-14 ${isRTL ? 'text-center' : ''}`}>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-[#5856D6]/10 rounded-full mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-1.5 h-1.5 bg-[#5856D6] rounded-full"></div>
            <span className="text-[11px] font-semibold text-[#5856D6] uppercase tracking-wide font-inter">
              {t.dashboardPreview.badge}
            </span>
          </div>
          <h2 className="font-dm text-3xl md:text-4xl lg:text-[42px] font-semibold tracking-tight text-[#1D1D1F] mb-4 leading-tight">
            {t.dashboardPreview.title}
          </h2>
          <p className="text-lg text-[#6e6e73] font-inter max-w-2xl mx-auto">
            {t.dashboardPreview.subtitle}
          </p>
        </div>

        {/* Dashboard Mockup */}
        <div className="relative max-w-4xl mx-auto">
          {/* Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0071E3]/10 via-[#5856D6]/10 to-[#34C759]/10 blur-3xl scale-110 rounded-3xl"></div>
          
          {/* Dashboard Card */}
          <div className="relative bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-[#E5E5E5]/60 overflow-hidden">
            {/* Header Bar */}
            <div className="bg-[#F5F5F7] px-6 py-4 border-b border-[#E5E5E5]/60">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#FEBC2E]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#28C840]"></div>
                </div>
                <span className="text-[#86868b] text-[13px] font-inter">Qykfin Dashboard</span>
                <div className="w-16"></div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Metrics Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {metrics.map((metric, index) => (
                  <div 
                    key={index}
                    className={`bg-[#F5F5F7] rounded-2xl p-5 ${isRTL ? 'text-right' : ''}`}
                  >
                    <div className={`flex items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      <metric.icon className="w-4 h-4 text-[#6e6e73]" />
                      <span className="text-[#6e6e73] text-[12px] font-inter">{metric.label}</span>
                    </div>
                    <p className="font-dm text-xl font-semibold text-[#1D1D1F] mb-1">{metric.value}</p>
                    <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      {metric.positive ? (
                        <TrendingUp className="w-3.5 h-3.5 text-[#34C759]" />
                      ) : (
                        <TrendingDown className="w-3.5 h-3.5 text-[#FF3B30]" />
                      )}
                      <span className={`text-[12px] font-inter ${metric.positive ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                        {metric.change}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Accounts List */}
              <div className="space-y-3">
                {accounts.map((account, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-4 bg-[#F5F5F7] rounded-xl ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${account.color}15` }}
                      >
                        <account.icon className="w-5 h-5" style={{ color: account.color }} />
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <p className="font-inter font-medium text-[#1D1D1F] text-[15px]">{account.name}</p>
                        <p className="text-[#86868b] text-[13px] font-inter capitalize">{account.type}</p>
                      </div>
                    </div>
                    <p className={`font-dm font-semibold text-[#1D1D1F] ${account.type === 'liability' ? '' : 'text-[#34C759]'}`}>
                      {account.balance}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPreview;
